import React from "react";
import {
  Building2, Laptop, LifeBuoy, Megaphone, FileCheck, Plus,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Input, Field, Select, Textarea } from "../../components/forms";
import { Stack, Row, Hint, StatusBadge } from "./kit";
import { useHrms, useFetch, fmtDate, fullName } from "./useHrms";

// HRMS ▸ Workplace — assets, help desk, announcements & policies.

const TABS = [
  { key: "assets", label: "Assets", icon: <Laptop size={14} /> },
  { key: "helpdesk", label: "Help desk", icon: <LifeBuoy size={14} /> },
  { key: "announcements", label: "Announcements", icon: <Megaphone size={14} /> },
  { key: "policies", label: "Policies", icon: <FileCheck size={14} /> },
];

export default function HrmsWorkplace() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("assets");

  return (
    <PageShell
      eyebrow="HRMS"
      title="Workplace"
      subtitle="Assets, help desk and the org noticeboard"
      icon={<Building2 size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "assets" && <AssetsTab t={t} />}
      {tab === "helpdesk" && <HelpdeskTab t={t} />}
      {tab === "announcements" && <AnnouncementsTab t={t} />}
      {tab === "policies" && <PoliciesTab t={t} />}
    </PageShell>
  );
}

function AssetsTab({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading, refresh } = useFetch("/assets");
  const employees = useFetch(isHr ? "/employees" : null);
  const [form, setForm] = React.useState({ name: "", category: "laptop", serialNumber: "" });
  const [assign, setAssign] = React.useState({ assetId: "", holderId: "" });

  const add = async () => {
    if (!form.name.trim()) return;
    await post("/assets", form);
    setForm({ name: "", category: "laptop", serialNumber: "" });
    refresh();
  };
  const doAssign = async () => {
    if (!assign.assetId || !assign.holderId) return;
    await post(`/assets/${assign.assetId}/assign`, { holderId: assign.holderId });
    setAssign({ assetId: "", holderId: "" });
    refresh();
  };
  const doReturn = async (id) => {
    await post(`/assets/${id}/return`, {});
    refresh();
  };

  const rows = data || [];
  const free = rows.filter((a) => a.status === "in_stock");

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Assets" padding={0} actions={<Badge tone="neutral">{rows.length} total · {free.length} in stock</Badge>}>
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Asset" },
            { key: "category", header: "Category", render: (r) => <Badge tone="neutral">{cap(r.category)}</Badge> },
            { key: "serialNumber", header: "Serial", render: (r) => r.serialNumber || "—" },
            { key: "holder", header: "Holder", render: (r) => r.holderId ? (
              <Row gap={8}><Avatar name={fullName(r.holderId)} src={r.holderId?.avatar} size={22} /><span>{fullName(r.holderId)}</span></Row>
            ) : "—" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr && r.status === "allocated"
                ? <Button size="sm" variant="ghost" onClick={() => doReturn(r._id)}>Return</Button>
                : null,
            },
          ]}
          rows={rows}
          emptyState={<EmptyState title="No assets" description="Register laptops, monitors and access cards below." />}
        />
      </SectionCard>

      {isHr && (
        <Row gap={16} align="flex-start" wrap>
          <SectionCard title="Register asset" padding={16} style={{ flex: 1, minWidth: 320 }}>
            <Row gap={12} wrap align="flex-end">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder='MacBook Pro 14"' /></Field>
              <Field label="Category">
                <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {["laptop", "monitor", "phone", "access_card", "furniture", "other"].map((c) => <option key={c} value={c}>{cap(c)}</option>)}
                </Select>
              </Field>
              <Field label="Serial"><Input value={form.serialNumber} onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))} /></Field>
              <Button variant="primary" leftIcon={<Plus size={13} />} onClick={add}>Add</Button>
            </Row>
          </SectionCard>

          <SectionCard title="Assign asset" padding={16} style={{ flex: 1, minWidth: 320 }}>
            <Row gap={12} wrap align="flex-end">
              <Field label="Asset (in stock)">
                <Select value={assign.assetId} onChange={(e) => setAssign((f) => ({ ...f, assetId: e.target.value }))}>
                  <option value="">Select…</option>
                  {free.map((a) => <option key={a._id} value={a._id}>{a.name}{a.serialNumber ? ` · ${a.serialNumber}` : ""}</option>)}
                </Select>
              </Field>
              <Field label="Employee">
                <Select value={assign.holderId} onChange={(e) => setAssign((f) => ({ ...f, holderId: e.target.value }))}>
                  <option value="">Select…</option>
                  {(employees.data?.employees || []).map((u) => <option key={u.userId} value={u.userId}>{u.name}</option>)}
                </Select>
              </Field>
              <Button variant="primary" onClick={doAssign}>Assign</Button>
            </Row>
          </SectionCard>
        </Row>
      )}
    </Stack>
  );
}

function HelpdeskTab({ t }) {
  const { isHr, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/tickets");

  const setStatus = async (id, status) => {
    await patch(`/tickets/${id}`, { status });
    refresh();
  };

  const rows = data || [];
  const open = rows.filter((r) => ["open", "in_progress"].includes(r.status));

  return (
    <Stack gap={t.s[4]}>
      <Hint>Employees raise tickets from My HR ▸ Assets & tickets. HR works the queue here.</Hint>
      <SectionCard title="Tickets" padding={0} actions={<Badge tone={open.length ? "warning" : "positive"}>{open.length} open</Badge>}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Raised by", render: (r) => fullName(r.raisedBy) },
            { key: "category", header: "Category", render: (r) => <Badge tone="neutral">{cap(r.category)}</Badge> },
            { key: "subject", header: "Subject" },
            { key: "priority", header: "Priority", render: (r) => <Badge tone={r.priority === "high" ? "negative" : r.priority === "medium" ? "warning" : "neutral"}>{cap(r.priority)}</Badge> },
            { key: "when", header: "Raised", render: (r) => fmtDate(r.createdAt) },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr && r.status !== "closed" ? (
                <Row gap={4} justify="flex-end">
                  {r.status === "open" && <Button size="sm" variant="ghost" onClick={() => setStatus(r._id, "in_progress")}>Start</Button>}
                  {r.status !== "resolved" && <Button size="sm" variant="secondary" onClick={() => setStatus(r._id, "resolved")}>Resolve</Button>}
                  <Button size="sm" variant="ghost" onClick={() => setStatus(r._id, "closed")}>Close</Button>
                </Row>
              ) : null,
            },
          ]}
          rows={rows}
          emptyState={<EmptyState title="No tickets" description="HR, IT and payroll queries land here." />}
        />
      </SectionCard>
    </Stack>
  );
}

function AnnouncementsTab({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading, refresh } = useFetch("/announcements?kind=announcement");
  const [form, setForm] = React.useState({ title: "", body: "", category: "", pinned: false });

  const publish = async () => {
    if (!form.title.trim()) return;
    await post("/announcements", { ...form, kind: "announcement" });
    setForm({ title: "", body: "", category: "", pinned: false });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      {isHr && (
        <SectionCard title="Publish announcement" padding={16}>
          <Stack gap={t.s[3]}>
            <Row gap={12} wrap>
              <Field label="Title"><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Office closed on Friday" /></Field>
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Holiday" /></Field>
            </Row>
            <Field label="Body"><Textarea rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></Field>
            <Row gap={8}><Button variant="primary" onClick={publish}>Publish</Button></Row>
          </Stack>
        </SectionCard>
      )}

      <SectionCard title="Announcements" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "title", header: "Title", render: (r) => (
              <div>
                <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{r.title}{r.pinned ? " 📌" : ""}</div>
                {r.body && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>{r.body.slice(0, 120)}</div>}
              </div>
            ) },
            { key: "category", header: "Category", render: (r) => r.category ? <Badge tone="neutral">{r.category}</Badge> : "—" },
            { key: "by", header: "By", render: (r) => fullName(r.createdBy) },
            { key: "publishedAt", header: "Published", render: (r) => fmtDate(r.publishedAt) },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="Quiet in here" description="HR announcements show for the whole org." />}
        />
      </SectionCard>
    </Stack>
  );
}

function PoliciesTab({ t }) {
  const { isHr, post, postForm } = useHrms();
  const { data, loading, refresh } = useFetch("/announcements?kind=policy");
  const headcount = useFetch("/overview/snapshot");
  const [form, setForm] = React.useState({ title: "", body: "", category: "", version: "1.0" });
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const publish = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await postForm("/announcements", { ...form, kind: "policy" }, file);
      setForm({ title: "", body: "", category: "", version: "1.0" });
      setFile(null);
      refresh();
    } finally { setBusy(false); }
  };
  const acknowledge = async (id) => {
    await post(`/announcements/${id}/acknowledge`, {});
    refresh();
  };

  const total = headcount.data?.headcount?.total || 0;

  return (
    <Stack gap={t.s[4]}>
      {isHr && (
        <SectionCard title="Publish policy" padding={16}>
          <Stack gap={t.s[3]}>
            <Row gap={12} wrap>
              <Field label="Title"><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Remote work policy" /></Field>
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></Field>
              <Field label="Version"><Input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} /></Field>
            </Row>
            <Field label="Body / summary"><Textarea rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></Field>
            <Field label="Policy document" hint="Optional PDF — stored securely, employees get a view link">
              <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Field>
            <Row gap={8}><Button variant="primary" onClick={publish} disabled={busy}>{busy ? "Publishing…" : "Publish"}</Button></Row>
          </Stack>
        </SectionCard>
      )}

      <SectionCard title="Policies" subtitle="Acknowledgement is tracked per employee" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "title", header: "Policy" },
            { key: "version", header: "Version", render: (r) => r.version || "—" },
            {
              key: "file", header: "File", render: (r) => r.fileUrl
                ? <a href={r.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: t.font.size.sm, color: t.c.primary }}>View</a>
                : "—",
            },
            { key: "ack", header: "Acknowledged", render: (r) => {
              const n = (r.acknowledgedBy || []).length;
              return <Badge tone={total > 0 && n >= total ? "positive" : "neutral"}>{n}{total ? ` / ${total}` : ""}</Badge>;
            } },
            { key: "publishedAt", header: "Published", render: (r) => fmtDate(r.publishedAt) },
            {
              key: "act", header: "", align: "right",
              render: (r) => r.acknowledgedByMe
                ? <Badge tone="positive">You acknowledged</Badge>
                : <Button size="sm" variant="secondary" onClick={() => acknowledge(r._id)}>Acknowledge</Button>,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No policies" description="Publish policy documents for the org to acknowledge." />}
        />
      </SectionCard>
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
